import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { RouteBuilderDraft } from "../../../../../types/routeBuilder";
import routeBuilder from "../../../../../services/axios/routeBuilder/index";
import flash from "../../../../../services/flash";
import { flashRouteBuilderError } from "../lib/flashRouteBuilderError";
import { draftsQueryKey } from "./useRouteBuilderDrafts";

/** Company drafts list for the Routes page. Does not touch the open builder session. */
export function useDraftsList(companyId: string) {
  const queryClient = useQueryClient();
  const draftsKey = useMemo(() => draftsQueryKey(companyId), [companyId]);
  const draftsQuery = useQuery({
    queryKey: draftsKey,
    queryFn: () => routeBuilder.listDrafts(companyId),
    enabled: Boolean(companyId),
  });

  const deleteMutation = useMutation({
    mutationFn: async (draftId: string) => {
      const current =
        queryClient.getQueryData<RouteBuilderDraft[]>(draftsKey) ?? [];
      const existing = current.find((draft) => draft.id === draftId);
      await routeBuilder.deleteDraft(draftId);
      return existing?.name;
    },
    onSuccess: (name) => {
      void queryClient.invalidateQueries({ queryKey: draftsKey });
      void flash.success(name ? `Deleted draft “${name}”.` : "Draft deleted.");
    },
    onError: (err) => {
      console.error(err);
      flashRouteBuilderError(err, "Failed to delete draft");
    },
  });

  return {
    drafts: draftsQuery.data ?? [],
    isLoading: draftsQuery.isLoading,
    isDeleting: deleteMutation.isPending,
    deleteDraft: deleteMutation.mutateAsync,
  };
}
